import { TDrawResult } from '@daymade/api-contracts';
import Chevron from 'components/Chevron';
import PaddedMaxWidthContainer from 'components/layout/PaddedMaxWidthContainer';
import DrawResult from 'components/results/DrawResult';
import NoResultsMessage from 'components/results/NoResultsMessage';
import { MainDrawScheduleCode } from 'constants/Draw';
import Link from 'next/link';
import { useState } from 'react';
import { ASSET_PREFIX, colors } from 'utils/constants';
import ToggleDrawCode from './ToggleDrawCode';

const TUESDAY = MainDrawScheduleCode.TUESDAY;
const FRIDAY = MainDrawScheduleCode.FRIDAY;

interface PreviousResultsProps {
  drawResults: TDrawResult[];
  setDrawResults: (drawResults: TDrawResult[]) => void;
  getDrawResultsForWeeklyDraw: (
    drawCode?: string,
    limit?: number
  ) => Promise<TDrawResult[]>;
}

const PreviousResults = ({
  drawResults,
  setDrawResults,
  getDrawResultsForWeeklyDraw,
}: PreviousResultsProps) => {
  const [resultsExpanded, setResultsExpanded] = useState(false);
  const [allResultsFetched, setAllResultsFetched] = useState(false);
  const [selectedDrawCode, setSelectedDrawCode] = useState(FRIDAY);

  return (
    <PaddedMaxWidthContainer additionalClassName="previousResults">
      <p style={{ marginTop: '1em' }}>
        Played one of our competitions recently and not checked the results? See
        if you’ve got winning numbers in our online competition below! On this
        page, you’ll find competition results for all our recent draws. And with
        hundreds of{' '}
        <Link href="/winners">
          <a style={{ textDecoration: 'underline' }}>winners</a>
        </Link>{' '}
        every single week, what’s stopping you from being the next?
      </p>

      <ToggleDrawCode
        selectedDrawCode={selectedDrawCode}
        handleToggle={async () => {
          const currentDrawCode =
            selectedDrawCode === FRIDAY ? TUESDAY : FRIDAY;
          const resultsForDrawDay = await getDrawResultsForWeeklyDraw(
            currentDrawCode,
            5
          );
          setDrawResults(resultsForDrawDay);
          setSelectedDrawCode(currentDrawCode);
        }}
      />

      {selectedDrawCode === TUESDAY && (
        <p style={{ fontSize: '1em', textAlign: 'center', maxWidth: '320px' }}>
          <em>
            Tuesday draws are carried out using an independent{' '}
            <a href="https://www.random.org/" target="_blank">
              3rd party
            </a>
          </em>
        </p>
      )}

      {selectedDrawCode === FRIDAY && (
        <p style={{ fontSize: '1em', textAlign: 'center' }}>
          <em>
            Friday draws take place live on{' '}
            <a href="https://www.instagram.com/daymadeuk/" target="_blank">
              Instagram
            </a>
          </em>
        </p>
      )}

      <div className="drawResultsContainer fullWidth flexCentered">
        {(!drawResults || !drawResults.length) && (
          <>
            <img
              src={`${ASSET_PREFIX}/images/loading.png`}
              style={{ width: '100px' }}
            />
          </>
        )}

        {drawResults &&
          drawResults
            .slice(1, resultsExpanded ? drawResults.length : 5)
            .map((result, index) => <DrawResult key={index} result={result} />)}
      </div>

      {drawResults?.length < 2 && <NoResultsMessage />}

      {drawResults?.length > 5 && (
        <div
          onClick={async () => {
            if (!allResultsFetched) {
              const allResults =
                await getDrawResultsForWeeklyDraw(selectedDrawCode);
              setDrawResults(allResults);
              setAllResultsFetched(true);
            }
            setResultsExpanded(!resultsExpanded);
          }}
          className="moreLessButton"
        >
          {resultsExpanded ? 'HIDE' : 'MORE'} RESULTS
          <Chevron
            direction="down"
            reversed={resultsExpanded}
            style={{
              color: colors.primaryBrandGray,
              fontSize: '1.6em',
              display: 'inline',
              marginTop: resultsExpanded ? '0.5em' : 0,
              verticalAlign: 'middle',
            }}
          />
        </div>
      )}

      <style jsx>
        {`
          a {
            text-decoration: underline;
          }

          :global(.previousResults) {
            padding: 0 0 4em;
            text-align: center;
          }

          :global(.previousResults .separator) {
            margin: 1em 0;
          }

          :global(.previousResults .subheading) {
            max-width: 260px;
            margin-top: 2em;
          }

          :global(.previousResults .toggleDrawCode) {
            margin: 2em 0 1em;
          }

          .moreLessButton {
            display: flex;
            flex-direction: column;
            justify-content: flex-center;
            background-color: clear;
            font-weight: bold;
            letter-spacing: 0em;
            cursor: pointer;
            align-items: center;
            text-align: center;
            margin-top: 2.5em;
          }

          :global(.previousResults .noResultsMessage) {
            margin-top: 2em;
          }

          :global(.previousResults .toggleDrawCode) {
            margin-bottom: 2.5em;
          }

          @media (min-width: 800px) {
            :global(.previousResults .subheading) {
              max-width: 285px;
            }
          }

          @media (min-width: 1100px) {
            :global(.previousResults .subheading) {
              max-width: 305px;
            }

            .drawResultsContainer {
              flex-direction: row;
              flex-wrap: wrap;
              margin-top: 1em;
            }

            :global(.previousResults .drawResultsContainer .drawResult) {
              width: 48%;
              margin: 1%;
            }
          }
        `}
      </style>
    </PaddedMaxWidthContainer>
  );
};

export default PreviousResults;
